
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import ColorPicker from '@/common/components/UI/ColorPicker/index.vue'

// types
import { DispatcherSectorT } from '@/core/types/dispatchers_sector.types'
import { SectorT } from '@/core/types/sectors.types'
import { CityT } from '@/core/types/cities.types'

// constants
import { DEFAULT_SECTOR_COLOR } from '@/core/constants/dispatcher.constants'

// realisations
import { dispatchers_sectors } from '@/core/realisations/dispatchers_sectors.realisation'
import { DataState } from '@/core/classes/DataState.class'
import { users } from '@/core/realisations/users.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { cities } from '@/core/realisations/cities.realisation'
import { UserT } from '@/core/types/users.types'

export default defineComponent({
    components: {
        'the-input': TheInput,
        'the-select': TheSelect,
        'the-button': TheButton,
        'color-picker': ColorPicker,
    },
    props: {
        sector: {
            type: Object as PropType<DispatcherSectorT | SectorT>,
            default: null,
        },
        unfinishedSector: {
            type: Object as PropType<{ color: string }>,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        isCourierSector: {
            type: Boolean,
            default: false,
        },
        isDispatcherSector: {
            type: Boolean,
            default: false,
        },
        isAddSector: {
            type: Boolean,
            default: false,
        },
        isPaintStarted: {
            type: Boolean,
            default: false,
        },
        isSectorPainted: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['cancel', 'save', 'start-paint', 'create'],
    setup(props, { emit }) {
        const state = reactive({
            name: null as string | null,
            color: '#8D12F3',
            description: '',
            dispatchers_sector_id: null as number | null,
            cityId: null as number | null,
            userIds: [] as number[],
        })

        const data_state = reactive({
            dispatcher_sectors: new DataState<DispatcherSectorT>({}),
            cities: new DataState<CityT>({}),
            dispatchers: new DataState<UserT>({}),
        })

        const StateValidationRules = {
            name: { required },
            description: {
                required: requiredIf(() => {
                    return !!props.isDispatcherSector
                }),
            },
            dispatchers_sector_id: {
                required: requiredIf(() => {
                    return !!props.isCourierSector
                }),
            },
        }

        const state_form$ = useVuelidate(StateValidationRules, state)

        const unfinishedSectorName = computed(() => {
            if (props.unfinishedSector) {
                if (Boolean(state.name) && state.name !== '') {
                    return state.name
                }

                return 'Сектор без названия'
            }

            return null
        })

        const loadDispatcherSectors = async () => {
            try {
                data_state.dispatcher_sectors.is_loading = true

                const response = await dispatchers_sectors.getList()
                data_state.dispatcher_sectors.list = response.data

                data_state.dispatcher_sectors.is_failed = false
                data_state.dispatcher_sectors.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.dispatcher_sectors.failure.catchError(error)

                if (!data_state.dispatcher_sectors.failure.error_state.is_cancel) {
                    data_state.dispatcher_sectors.is_failed = true
                }
            } finally {
                if (!data_state.dispatcher_sectors.failure.error_state.is_cancel) {
                    data_state.dispatcher_sectors.is_loading = false
                }
            }
        }

        // CITIES
        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadMoreCities = async (page: number) => {
            if (page > data_state.cities.pagination.total_pages) return
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.cities.list = data_state.cities.list.concat(response.data)

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const searchCity = (search_query: string) => {
            // data_state.cities.pagination.current_page
            loadCities(1, search_query)
        }

        const unshiftFoundCity = async (city: CityT) => {
            await loadCities(data_state.cities.pagination.current_page)
            data_state.cities.list.unshift(city)
        }

        // DISPATCHERS
        const loadDispatchers = async (page: number) => {
            data_state.dispatchers.pagination.current_page = page

            try {
                data_state.dispatchers.is_loading = true

                const data = await users.getDispatchers(page)
                data_state.dispatchers.list = data.data

                data_state.dispatchers.pagination.total_pages = data.meta.last_page

                data_state.dispatchers.is_failed = false
                data_state.dispatchers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.dispatchers.failure.catchError(error)

                if (!data_state.dispatchers.failure.error_state.is_cancel) {
                    data_state.dispatchers.is_failed = true
                }
            } finally {
                if (!data_state.dispatchers.failure.error_state.is_cancel) {
                    data_state.dispatchers.is_loading = false
                }
            }
        }

        const loadMoreDispatchers = async (page: number) => {
            if (page > data_state.dispatchers.pagination.total_pages) return
            data_state.dispatchers.pagination.current_page = page

            try {
                data_state.dispatchers.is_loading = true

                const response = await users.getDispatchers(page)
                data_state.dispatchers.list = data_state.dispatchers.list.concat(response.data)

                data_state.dispatchers.pagination.total_pages = response.meta.last_page

                data_state.dispatchers.is_failed = false
                data_state.dispatchers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.dispatchers.failure.catchError(error)

                if (!data_state.dispatchers.failure.error_state.is_cancel) {
                    data_state.dispatchers.is_failed = true
                }
            } finally {
                if (!data_state.dispatchers.failure.error_state.is_cancel) {
                    data_state.dispatchers.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            if (props.isAddSector && props.isCourierSector) {
                loadDispatcherSectors()
            }

            if (props.isDispatcherSector) {
                state.color = DEFAULT_SECTOR_COLOR
                loadCities(1)
                loadDispatchers(1)
            }

            if (!props.sector) return

            state.name = props.sector.name

            if ('color' in props.sector) {
                state.color = props.sector.color
            }

            if ('description' in props.sector) {
                state.description = props.sector.description
            }

            if ('dispatchers_sector_id' in props.sector) {
                state.dispatchers_sector_id = props.sector.dispatchers_sector_id
            }

            if ('city' in props.sector) {
                if (props.sector.city) {
                    state.cityId = props.sector.city.id
                }
            }

            if ('users' in props.sector) {
                if (props.sector.users) {
                    // @ts-ignore
                    state.userIds = props.sector.users.map((user) => user.id)
                }
            }
        })

        const emitCancel = () => {
            if (props.loading) return

            emit('cancel')
        }

        const emitSave = () => {
            emit('save', {
                name: state.name,
                color: state.color,
                description: state.description,
                cityId: state.cityId,
                userIds: state.userIds,
            })
        }

        const emitCreate = () => {
            emit('create', {
                name: state.name,
                color: state.color,
                description: state.description,
                dispatchers_sector_id: state.dispatchers_sector_id,
            })
        }

        const emitManager = () => {
            if (props.loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (!props.isAddSector) {
                emitSave()
                return
            }

            if (props.isAddSector) {
                emitCreate()
                return
            }
        }

        const emitStartPaint = () => {
            emit('start-paint', { color: state.color })
        }

        return {
            state,
            data_state,
            state_form$,
            loadDispatcherSectors,
            emitCancel,
            emitManager,
            emitStartPaint,
            unfinishedSectorName,
            // cities
            loadCities,
            loadMoreCities,
            searchCity,
            unshiftFoundCity,
            // dispatchers
            loadDispatchers,
            loadMoreDispatchers,
        }
    },
})
